import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import WarningIcon from '@mui/icons-material/Warning';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  getSitesInfoQueryKey,
  useSitesInfo,
  useStagingSitePromote,
} from '@newfold/huapi-js';
import { SitesInfo200 } from '@newfold/huapi-js/src/index.schemas';

import { useAlert } from '~/components/Alerts/alertsStore';
import LoadingButtonWrapper from '~/components/LoadingButtonWrapper';
import { invalidateMultipleQueries } from '~/components/MFEProvider/lib/HttpClient/queryClient';
import { useTenant } from '~/components/TenantProvider';
import { eventWatchRequestPropOptions } from '~/hooks/useAccountEvents';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import useResponsive from '~/hooks/useResponsive';
import { SiteInfoType } from '~/types/site';
import { setAlertMessage } from '~/utils/common/helpers';

import {
  EVENT_PROMOTE_DONE,
  PROMOTION_FAILED,
  PROMOTION_IN_PROGRESS,
} from '../../../utils/constants';
import { checkIsStagingSite } from '../../../utils/helpers';
import { getStagingPromotionQueryKeys } from '../utils/helpers';
import PromoteInProgressModal from './PromoteInProgressModal';

export type PromoteModalPropsOptions = {
  open: boolean;
  handleClose: Function;
  parentDomain: string;
  stagingSite: SiteInfoType;
};

const PromoteModal = ({
  open,
  handleClose,
  parentDomain,
  stagingSite,
}: PromoteModalPropsOptions) => {
  const { t } = useTranslation('cloud', {
    keyPrefix: 'staging.promoteSite.promoteModal',
  });

  const { isTablet } = useResponsive();
  const isResponsive = isTablet();

  const generateAlert = useAlert();

  const navigate = useNavigate();
  const location = useLocation();

  const { cloudStagingPromoteKb, phoneNumberFormatted } = useBrandInfo();

  const [showProgressModal, setShowProgressModal] = useState(false);
  const [pollingActive, setPollingActive] = useState(false);
  const [siteInfoRetryCount, setSiteInfoRetryCount] = useState(0);
  const siteInfoRefetchInterval = 5 * 1000; // 5 seconds
  const maxSiteInfoRetries = 12; // 12 * 5 sec apart -> poll for 60 sec max

  const stagingSiteId = stagingSite?.id!;
  const parentSiteId = stagingSite?.staging?.parent_site_id!;
  const { callbacks } = useTenant();

  const queryClient = useQueryClient();
  const newQueryKeys: any[] = getStagingPromotionQueryKeys(
    parentSiteId,
    stagingSiteId,
    stagingSite?.account_back_ref!,
  );

  const {
    mutate: promoteSite,
    isPending,
    isSuccess: isSuccessSitePromote,
  } = useStagingSitePromote({
    mutation: {
      onError: () => {
        generateAlert({
          severity: 'error',
          description: (
            <Trans
              i18nKey={t('alertMessage.error')}
              values={{ phoneNumber: phoneNumberFormatted }}
            />
          ),
          showCloseBtn: true,
          persist: true,
        });
        removeQueryKeysAndCloseModals();
      },
    },
  });

  const hasPromoteJob = (site: SiteInfoType): boolean =>
    !!site?.jobs && Object.values(site.jobs).includes('promote');

  const checkIsPromotionInProgress = (site: SiteInfoType): boolean =>
    (!!site?.staging?.promoting_from &&
      site?.staging?.promotion_status === PROMOTION_IN_PROGRESS) ||
    // or is there a promote job, but it hasn't started processing yet?
    (checkIsStagingSite(site) && hasPromoteJob(site));

  // This keeps any UI updates from occurring while polling, until the modal closes
  const customSiteInfoQueryKey = [
    ...getSitesInfoQueryKey(Number(stagingSiteId)),
    '/promote-site-info',
  ];

  const { data: siteInfo, isSuccess: isSuccessSiteInfo } = useSitesInfo(
    Number(stagingSiteId),
    {
      query: {
        queryKey: customSiteInfoQueryKey,
        enabled:
          !!stagingSiteId &&
          showProgressModal &&
          pollingActive &&
          isSuccessSitePromote,
        refetchInterval: (query) => {
          const site: SitesInfo200 | undefined = query?.state?.data?.data;
          return !!site &&
            // If we see it as 1) currently promoting, or 2) not yet having started promotion
            (checkIsPromotionInProgress(site) || checkIsStagingSite(site)) &&
            siteInfoRetryCount <= maxSiteInfoRetries
            ? siteInfoRefetchInterval
            : false;
        },
      },
    },
  );

  useEffect(() => {
    if (isSuccessSitePromote) {
      //enable polling as soon as receiving job id
      setPollingActive(true);
    }
  }, [isSuccessSitePromote]);

  useEffect(() => {
    if (isSuccessSiteInfo && pollingActive) {
      // Increment retry counter
      setSiteInfoRetryCount((currentCounter) => currentCounter + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteInfo]);

  const redirectAfterPromotion = () => {
    // If we started in a parent site's staging list, go to the new parent site's staging list
    if (location.pathname.includes('staging'))
      navigate(`/sites/${stagingSiteId}/staging`);
  };

  useEffect(() => {
    // We only want this to run if each api call (not the promotion process itself) was successful
    if (isSuccessSiteInfo && isSuccessSitePromote) {
      const isPromotionComplete =
        siteInfo?.data?.staging?.promotion_status === null &&
        siteInfo?.data?.staging?.promoted_from !== null;

      const isPromotionStable =
        // Is promomtion complete?
        isPromotionComplete ||
        // or is promotion in progress?
        checkIsPromotionInProgress(siteInfo?.data);

      const isPromotionFailed =
        siteInfo?.data?.staging?.promotion_status === PROMOTION_FAILED;

      // if the backend query is completed first irrespective of polling complete
      if (isPromotionStable && !isPromotionFailed) {
        // If we're all finished here
        if (isPromotionComplete) {
          generateAlert({
            severity: 'info',
            description: (
              <Trans
                t={t}
                i18nKey={'alertMessage.success'}
                values={{ siteName: stagingSite?.name }}
                components={{ b: <b /> }}
              />
            ),
            showCloseBtn: true,
          });

          // Clean up the counter if we finish before max retries
          setSiteInfoRetryCount(0);

          removeQueryKeysAndCloseModals();

          redirectAfterPromotion();
        }
        // if polling is complete but backend update have not been completed
        else if (!pollingActive || siteInfoRetryCount >= maxSiteInfoRetries) {
          callbacks.subscribeToEvent({
            events: [EVENT_PROMOTE_DONE],
            resourceIds: {
              hosting: [String(siteInfo?.data?.account_id)],
              site: [String(stagingSiteId), String(parentSiteId)],
            },
            queryKeys: newQueryKeys,
          } as eventWatchRequestPropOptions);

          generateAlert({
            severity: 'info',
            description: t('alertMessage.polling', {
              siteName: stagingSite?.name,
            }),
            showCloseBtn: true,
            persist: true,
          });

          // Clean up the counter if we hit max retries
          setSiteInfoRetryCount(0);

          removeQueryKeysAndCloseModals();

          redirectAfterPromotion();
        }
      }

      if (isPromotionFailed) {
        setAlertMessage('error', t('alertMessage.error'));
        removeQueryKeysAndCloseModals();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    siteInfo,
    showProgressModal,
    isSuccessSiteInfo,
    isSuccessSitePromote,
    pollingActive,
  ]);

  // Close promotion modals and reset state values
  const closeModals = () => {
    setPollingActive(false);
    setShowProgressModal(false);
    handleClose();
  };

  const removeQueryKeysAndCloseModals = () => {
    //remove query keys to allow parent components to reload on mount. Helps with navigating to the same page.
    invalidateMultipleQueries(queryClient, newQueryKeys);

    queryClient.removeQueries({ queryKey: customSiteInfoQueryKey });

    closeModals();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleSubmit = () => {
    setShowProgressModal(true);
    promoteSite({ siteId: stagingSiteId as number, data: {} });
  };

  if (showProgressModal) {
    return <PromoteInProgressModal open={open} />;
  }

  return (
    <>
      <DialogTitle>
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          {isResponsive && <WarningIcon color="warning" />}
          <div>{t('title')}</div>
        </Stack>
      </DialogTitle>
      <DialogContent
        data-testid={'promote-modal-description'}
        aria-label={'promote staging modal description'}
      >
        <Divider />
        <Stack spacing={4} pt={2}>
          <Stack direction="row" spacing={2}>
            {!isResponsive && <WarningIcon color="warning" />}
            {/* Promotion description paragraphs */}
            <Stack spacing={2}>
              <Typography
                component="div"
                variant={isResponsive ? 'body2' : 'body1'}
              >
                <Trans
                  t={t}
                  i18nKey="desc.para1"
                  values={{ domain: stagingSite?.domain, parentDomain }}
                  components={{
                    b: <b />,
                  }}
                />
              </Typography>

              <Typography
                component="div"
                variant={isResponsive ? 'body2' : 'body1'}
              >
                <Trans
                  t={t}
                  i18nKey={
                    cloudStagingPromoteKb
                      ? 'desc.para2'
                      : 'desc.para2WithoutLink'
                  }
                  components={{
                    Link: <Link href={cloudStagingPromoteKb} target="_blank" />,
                  }}
                />
              </Typography>
            </Stack>
          </Stack>
          {/* Domain swap images */}
          <Stack spacing={1} alignItems={'center'}>
            <Stack
              alignItems={'center'}
              bgcolor={'secondary.main'}
              borderRadius={2}
              justifyContent={'center'}
              paddingX={isResponsive ? 2 : 0}
              paddingY={1}
              width={isResponsive ? '90%' : '70%'}
            >
              <Typography
                variant={isResponsive ? 'h4' : 'h3'}
                sx={{ wordBreak: 'break-word' }}
                color={'common.white'}
              >
                {parentDomain}
              </Typography>
            </Stack>
            <Stack direction={'row'} spacing={1}>
              <ArrowUpwardIcon color={'action'} fontSize={'large'} />
              <ArrowDownwardIcon color={'action'} fontSize={'large'} />
            </Stack>
            <Stack
              alignItems={'center'}
              bgcolor={'warning.A200'}
              borderRadius={2}
              paddingX={isResponsive ? 2 : 0}
              paddingY={1}
              width={isResponsive ? '90%' : '70%'}
            >
              <Typography
                variant={isResponsive ? 'h4' : 'h3'}
                sx={{ wordBreak: 'break-word' }}
              >
                {stagingSite?.domain}
              </Typography>
            </Stack>
          </Stack>
          {/* Domain blurb */}
          <Typography
            component="div"
            variant={isResponsive ? 'body2' : 'body1'}
          >
            {t('desc.para3')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack spacing={2} direction={'row'}>
          <Button
            aria-label={'promote cancel button'}
            data-testid={'promote-cancel-button'}
            variant="text"
            onClick={handleCancel}
            disabled={isPending}
          >
            {t('cancelBtn')}
          </Button>
          <LoadingButtonWrapper
            aria-label={'promote go live button'}
            data-testid={'promote-go-live-button'}
            variant="contained"
            loading={isPending}
            onClick={handleSubmit}
          >
            {t('goLiveBtn')}
          </LoadingButtonWrapper>
        </Stack>
      </DialogActions>
    </>
  );
};
export default PromoteModal;
